"use client";

import { useState } from "react";
import { unstable_isUnrecognizedActionError } from "next/navigation";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { track } from "@/lib/analytics/tracker";
import { captureError } from "@/lib/error-tracking";
import { navigateToDocument, reloadDocument } from "@/lib/document-navigation";
import { hasSignedInSession } from "@/components/auth/sign-in-actions";
import { reportSignInConversion, type SignInSurface } from "@/lib/auth/sign-in/conversion";
import type { SignedIn, SignInResult } from "@/lib/auth/sign-in/result";
import type { AuthEntryPoint, AuthMethod } from "@/lib/analytics/types";
import {
  trackAuthCompleted,
  trackAuthFailure,
  type AuthAttempt,
} from "@/lib/auth/sign-in/auth-tracking";

const FAILED_MESSAGE = "We couldn't finish signing you in. Please try again.";
const MAX_TRIES = 2;
const FAILED_CODE_MESSAGE =
  "We couldn't finish signing you in. Send yourself a new code and try again.";

function hostedSignInUrl(): string {
  const returnTo = `${window.location.pathname}${window.location.search}`;
  return `/sign-in?${new URLSearchParams({ returnTo }).toString()}`;
}

function reportUnfinished(
  attempt: AuthAttempt,
  result: Extract<SignInResult, { status: "error" | "needs_hosted" }>,
  setError: (message: string) => void,
): void {
  if (result.status === "needs_hosted") {
    trackAuthFailure(attempt, "verify", "needs_hosted");
    navigateToDocument(hostedSignInUrl());
    return;
  }
  trackAuthFailure(attempt, "verify", result.reason);
  setError(result.message);
}

interface AuthFailure {
  message: string;
  needsNewCode: boolean;
  offerEmailedLink: boolean;
}

function errorName(cause: unknown): string {
  if (cause instanceof Error) return cause.name || "Error";
  return typeof cause;
}

function recordThrown(attempt: AuthAttempt, cause: unknown, tryNumber: number): void {
  trackAuthFailure(attempt, "verify", "exception", { errorName: errorName(cause), attempt: tryNumber });
  captureError(cause, {
    tags: { area: "auth_completion", entry_point: attempt.entryPoint, method: attempt.method },
    extra: { attempt: tryNumber },
  });
}

async function reportThrown(
  surface: SignInSurface,
  setFailure: (failure: AuthFailure) => void,
): Promise<void> {
  if (await hasSignedInSession().catch(() => false)) {
    reloadDocument();
    return;
  }
  const needsNewCode = surface === "magic_code";
  setFailure({
    message: needsNewCode ? FAILED_CODE_MESSAGE : FAILED_MESSAGE,
    needsNewCode,
    offerEmailedLink: !needsNewCode,
  });
}

function useAuthFailure() {
  const [failure, setFailure] = useState<AuthFailure | null>(null);
  const setError = (message: string | null) =>
    setFailure(message === null ? null : { message, needsNewCode: false, offerEmailedLink: false });
  return { failure, setFailure, setError };
}

interface AuthCompletionOptions {
  entryPoint: AuthEntryPoint;
  surface: SignInSurface;
  method: AuthMethod;
  onSignedIn: (result: SignedIn) => void | Promise<void>;
  onNeedsCode?: () => void;
}

function useSettle(options: AuthCompletionOptions, attempt: AuthAttempt, setError: (message: string | null) => void) {
  const { refreshAuth } = useAuth();
  const complete = async (result: SignedIn) => {
    trackAuthCompleted(attempt, result.isNew);
    const refreshed = await refreshAuth().catch(() => ({ error: "refresh failed" }));
    if (refreshed?.error) {
      reloadDocument();
      return;
    }
    if (options.surface !== "password") {
      track("auth.code_verified", { entry_point: options.entryPoint, is_new: result.isNew });
    }
    reportSignInConversion(result.isNew, options.surface);
    await options.onSignedIn(result);
  };
  return async (result: SignInResult) => {
    if (result.status === "signed_in") await complete(result);
    else if (result.status === "needs_code") options.onNeedsCode?.();
    else reportUnfinished(attempt, result, setError);
  };
}

interface RetryHandlers {
  attempt: AuthAttempt;
  settle: (result: SignInResult) => Promise<void>;
  onGiveUp: () => Promise<void>;
}

async function giveUpOn(cause: unknown, handlers: RetryHandlers, tryNumber: number): Promise<void> {
  if (unstable_isUnrecognizedActionError(cause)) {
    reloadDocument();
    return;
  }
  recordThrown(handlers.attempt, cause, tryNumber);
  await handlers.onGiveUp();
}

async function attemptWithRetry(
  signInAttempt: () => Promise<SignInResult>,
  handlers: RetryHandlers,
  tryNumber = 1,
): Promise<void> {
  let result: SignInResult;
  try {
    result = await signInAttempt();
  } catch (cause) {
    const canRetry =
      !unstable_isUnrecognizedActionError(cause) &&
      tryNumber < MAX_TRIES &&
      !(await hasSignedInSession().catch(() => false));
    if (!canRetry) return giveUpOn(cause, handlers, tryNumber);
    recordThrown(handlers.attempt, cause, tryNumber);
    return attemptWithRetry(signInAttempt, handlers, tryNumber + 1);
  }
  await handlers.settle(result).catch((cause: unknown) => giveUpOn(cause, handlers, tryNumber));
}

export function useAuthCompletion(options: AuthCompletionOptions) {
  const { failure, setFailure, setError } = useAuthFailure();
  const [isBusy, setIsBusy] = useState(false);
  const attempt: AuthAttempt = { entryPoint: options.entryPoint, method: options.method };
  const settle = useSettle(options, attempt, setError);

  const run = async (signInAttempt: () => Promise<SignInResult>) => {
    setIsBusy(true);
    setError(null);
    try {
      await attemptWithRetry(signInAttempt, {
        attempt,
        settle,
        onGiveUp: () => reportThrown(options.surface, setFailure),
      });
    } finally {
      setIsBusy(false);
    }
  };

  return {
    error: failure?.message ?? null,
    needsNewCode: failure?.needsNewCode ?? false,
    offerEmailedLink: failure?.offerEmailedLink ?? false,
    setError,
    clearError: () => setFailure(null),
    isBusy,
    run,
  };
}
